// src/components/Common/ThumbnailStrip.jsx
import React, { useRef, useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Thumbs, FreeMode, Navigation } from "swiper";
// import { ArrowBackIosNew, ArrowForwardIos } from "@mui/icons-material";

import ArrowBackIos from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIos from "@material-ui/icons/ArrowForwardIos";

import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/thumbs";
import "swiper/css/navigation";

/**
 * Safe ThumbnailStrip with guarded Navigation initialization to avoid:
 * "Cannot convert undefined or null to object" from Swiper init.
 *
 * Props:
 * - images: [{ image: 'url' }, ...]
 * - isMobile: boolean
 * - setThumbsSwiper: function to set the thumbs swiper instance
 * - onThumbClick: (index) => void
 * - activeIndex: number
 */
export default function ThumbnailStrip({
    images = [],
    isMobile = false,
    setThumbsSwiper = () => { },
    onThumbClick = () => { },
    activeIndex = 0,
}) {
    const thumbCount = images.length;
    const mobileThumbSize = 72;
    const desktopThumbSize = thumbCount <= 2 ? 160 : 100;
    const thumbSize = isMobile ? mobileThumbSize : desktopThumbSize;

    const prevRef = useRef(null);
    const nextRef = useRef(null);

    // navigation object passed to Swiper; only set once refs exist
    const [navigation, setNavigation] = useState(false);

    // show arrows only when there are more thumbs than visible capacity
    const needArrows = thumbCount > (isMobile ? 3 : 5);

    // When refs mount, set navigation to actual DOM elements
    useEffect(() => {
        if (!needArrows) {
            setNavigation(false);
            return;
        }

        // Wait a tick to ensure refs have been attached to DOM
        const id = window.setTimeout(() => {
            if (prevRef.current && nextRef.current) {
                setNavigation({
                    prevEl: prevRef.current,
                    nextEl: nextRef.current,
                });
            }
        }, 0);

        return () => window.clearTimeout(id);
    }, [needArrows, prevRef.current, nextRef.current]); // eslint-disable-line

    // Swiper config values
    const slidesPerViewDefault = isMobile ? 3.5 : Math.min(5, thumbCount);
    const spaceBetweenDefault = isMobile ? 8 : 10;

    return (
        <div
            style={{
                position: "relative",
                width: "100%",
                padding: "8px 0",
                boxSizing: "border-box",
            }}
        >
            {/* Navigation buttons (render always but hide with CSS when not needed) */}
            {needArrows && (
                <>
                    <button
                        ref={prevRef}
                        className="thumb-nav-btn thumb-prev"
                        aria-label="Previous thumbnails"
                        type="button"
                    >
                        <ArrowBackIos fontSize="small" />
                    </button>
                    <button
                        ref={nextRef}
                        className="thumb-nav-btn thumb-next"
                        aria-label="Next thumbnails"
                        type="button"
                    >
                        <ArrowForwardIos fontSize="small" />
                    </button>
                </>
            )}

            <Swiper
                modules={[Thumbs, FreeMode, Navigation]}
                onSwiper={setThumbsSwiper}
                slidesPerView={slidesPerViewDefault}
                spaceBetween={spaceBetweenDefault}
                watchSlidesProgress
                freeMode={true}
                centeredSlides={false}
                slideToClickedSlide={true}
                navigation={navigation || false} // either an object or false
                style={{
                    padding: needArrows ? "0 36px" : "0", // leave space for arrow buttons when present
                    overflow: "visible",
                }}
                breakpoints={{
                    0: { slidesPerView: Math.min(3.5, thumbCount), spaceBetween: 8 },
                    480: { slidesPerView: Math.min(4, thumbCount), spaceBetween: 8 },
                    768: { slidesPerView: Math.min(thumbCount <= 2 ? thumbCount : 5, thumbCount), spaceBetween: 10 },
                    1024: { slidesPerView: Math.min(6, thumbCount), spaceBetween: 10 },
                }}
            >
                {images.map((item, idx) => (
                    <SwiperSlide
                        key={`thumb-${idx}`}
                        onClick={() => onThumbClick(idx)}
                        style={{
                            cursor: "pointer",
                            width: thumbSize,
                            height: thumbSize,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxSizing: "border-box",
                        }}
                    >
                        <div
                            style={{
                                border: idx === activeIndex ? "2px solid #fca700" : "2px solid transparent",
                                borderRadius: 8,
                                overflow: "hidden",
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "#fff",
                                padding: 6,
                            }}
                            className="thumb-wrapper"
                        >
                            <img
                                src={item.image}
                                alt={item.text || `thumb-${idx}`}
                                style={{
                                    height: "100%",
                                    width: "auto",
                                    display: "block",
                                    objectFit: "contain",
                                    objectPosition: "center",
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                }}
                                loading="lazy"
                                draggable={false}
                            />
                        </div>
                    </SwiperSlide>
                ))}
            </Swiper>

            <style>{`
        .thumb-nav-btn {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 20;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: rgba(255,255,255,0.95);
          border: 1px solid #e6e6e6;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.16s, transform 0.12s;
          box-shadow: 0 1px 4px rgba(0,0,0,0.06);
        }
        .thumb-nav-btn:hover { background: #fca700; color: #fff; transform: scale(1.03); }
        .thumb-prev { left: 6px; }
        .thumb-next { right: 6px; }

        /* Hide buttons if not needed (defensive) */
        ${!needArrows ? `.thumb-nav-btn { display: none; }` : ""}
      `}</style>
        </div>
    );
}







// import React from "react";
// import { Swiper, SwiperSlide } from "swiper/react";
// import { Thumbs, FreeMode } from "swiper";

// import "swiper/css";
// import "swiper/css/free-mode";
// import "swiper/css/thumbs";

// /**
//  * Props:
//  * - images: [{ image: 'url' }, ...]
//  * - isMobile: boolean
//  * - thumbsSwiper: current thumbs swiper instance (optional)
//  * - setThumbsSwiper: function to set the thumbs swiper instance
//  * - onThumbClick: function(index) -> called on click
//  * - activeIndex: number -> index of currently active main slide
//  */
// export default function ThumbnailStrip({
//     images = [],
//     isMobile = false,
//     thumbsSwiper,
//     setThumbsSwiper,
//     onThumbClick = () => { },
//     activeIndex = 0,
// }) {
//     const thumbCount = images.length;
//     const mobileThumbSize = 72;
//     const desktopThumbSize = thumbCount <= 2 ? 160 : 100;

//     if (isMobile) {
//         return (
//             <div
//                 style={{
//                     display: "flex",
//                     gap: 8,
//                     overflowX: "auto",
//                     WebkitOverflowScrolling: "touch",
//                     padding: "8px 4px",
//                     boxSizing: "border-box",
//                 }}
//                 className="mobile-thumb-row"
//             >
//                 {images.map((item, idx) => (
//                     <button
//                         key={`mobile-thumb-${idx}`}
//                         onClick={() => onThumbClick(idx)}
//                         style={{
//                             minWidth: mobileThumbSize,
//                             minHeight: mobileThumbSize,
//                             padding: 6,
//                             borderRadius: 8,
//                             border: idx === activeIndex ? "2px solid #fca700" : "2px solid transparent",
//                             background: "#fff",
//                             overflow: "hidden",
//                             display: "inline-flex",
//                             alignItems: "center",
//                             justifyContent: "center",
//                             boxSizing: "border-box",
//                             cursor: "pointer",
//                         }}
//                         aria-label={`thumbnail-${idx}`}
//                     >
//                         <img
//                             src={item.image}
//                             alt={item.text || `thumb-${idx}`}
//                             style={{
//                                 height: "100%",
//                                 width: "auto",
//                                 display: "block",
//                                 objectFit: "contain",
//                                 objectPosition: "center",
//                                 maxWidth: "100%",
//                                 maxHeight: "100%",
//                             }}
//                             loading="lazy"
//                             draggable={false}
//                         />
//                     </button>
//                 ))}
//             </div>
//         );
//     }

//     // Desktop / tablet: Swiper backed
//     const thumbSlidesPerView = thumbCount <= 2 ? thumbCount : Math.min(thumbCount, 5);
//     const thumbSpaceBetween = thumbCount <= 2 ? 16 : 8;

//     return (
//         <div style={{ width: "100%", boxSizing: "border-box", padding: "8px 0" }}>
//             <Swiper
//                 modules={[Thumbs, FreeMode]}
//                 onSwiper={setThumbsSwiper}
//                 slidesPerView={thumbSlidesPerView}
//                 spaceBetween={thumbSpaceBetween}
//                 watchSlidesProgress
//                 freeMode={true}
//                 centeredSlides={false}
//                 slideToClickedSlide={true}
//                 style={{ padding: 0, overflow: "visible" }}
//                 breakpoints={{
//                     0: { slidesPerView: Math.min(3, thumbCount), spaceBetween: 8 },
//                     480: { slidesPerView: Math.min(4, thumbCount), spaceBetween: 8 },
//                     768: { slidesPerView: thumbSlidesPerView, spaceBetween: thumbSpaceBetween },
//                     1024: { slidesPerView: Math.min(5, thumbCount), spaceBetween: thumbSpaceBetween },
//                 }}
//             >
//                 {images.map((item, idx) => (
//                     <SwiperSlide
//                         key={`thumb-${idx}`}
//                         onClick={() => onThumbClick(idx)}
//                         style={{
//                             cursor: "pointer",
//                             width: desktopThumbSize,
//                             height: desktopThumbSize,
//                             display: "flex",
//                             alignItems: "center",
//                             justifyContent: "center",
//                             boxSizing: "border-box",
//                             overflow: "visible",
//                         }}
//                     >
//                         <div
//                             style={{
//                                 border: idx === activeIndex ? "2px solid #fca700" : "2px solid transparent",
//                                 borderRadius: 8,
//                                 overflow: "hidden",
//                                 width: "100%",
//                                 height: "100%",
//                                 display: "flex",
//                                 alignItems: "center",
//                                 justifyContent: "center",
//                                 background: "#fff",
//                                 padding: 6,
//                                 boxSizing: "border-box",
//                             }}
//                             className="thumb-wrapper"
//                         >
//                             <img
//                                 src={item.image}
//                                 alt={item.text || `thumb-${idx}`}
//                                 style={{
//                                     height: "100%",
//                                     width: "auto",
//                                     display: "block",
//                                     objectFit: "contain",
//                                     objectPosition: "center",
//                                     maxWidth: "100%",
//                                     maxHeight: "100%",
//                                 }}
//                                 loading="lazy"
//                                 draggable={false}
//                             />
//                         </div>
//                     </SwiperSlide>
//                 ))}
//             </Swiper>
//         </div>
//     );
// }








// // ThumbnailStrip.jsx
// import React, { useRef } from "react";
// import { Swiper, SwiperSlide } from "swiper/react";
// import { Thumbs, Navigation } from "swiper";
// import "swiper/css";
// import "swiper/css/navigation";
// import "swiper/css/thumbs";

// /**
//  * Props:
//  * - images: [{ image: string, text?: string }]
//  * - mainSwiper: swiper instance (optional)
//  * - thumbSize: number|string (e.g. 64 or "64px")
//  * - containerWidth: number|string (e.g. 400 or "400px") -> max width of thumbnail area
//  * - thumbSlidesPerView: default slidesPerView at larger breakpoints
//  * - thumbSpaceBetween: spacing between thumbs
//  */
// export default function ThumbnailStrip({
//     images = [],
//     mainSwiper = null,
//     thumbSize = 72,
//     containerWidth = "420px",
//     thumbSlidesPerView = 5,
//     thumbSpaceBetween = 8,
// }) {
//     const thumbCount = images?.length || 0;
//     const thumbsRef = useRef(null);
//     const navPrevRef = useRef(null);
//     const navNextRef = useRef(null);

//     // numeric thumbSize for some style math
//     const thumbSizePx = typeof thumbSize === "number" ? `${thumbSize}px` : thumbSize;

//     return (
//         <div
//             className="thumbnail-strip-root"
//             style={{
//                 marginTop: 12,
//                 display: "flex",
//                 justifyContent: "center",
//             }}
//         >
//             <div
//                 className="thumbnail-strip-container"
//                 style={{
//                     width: "100%",
//                     maxWidth: containerWidth, // control fixed width here
//                     boxSizing: "border-box",
//                     padding: "8px 0",
//                     position: "relative", // for absolutely positioned arrows
//                 }}
//             >
//                 {thumbCount <= 2 ? (
//                     // centered non-swiper layout for 1-2 thumbs
//                     <div
//                         style={{
//                             display: "flex",
//                             gap: 16,
//                             justifyContent: "center",
//                             alignItems: "center",
//                             width: "100%",
//                             boxSizing: "border-box",
//                             padding: "8px 0",
//                         }}
//                     >
//                         {images.map((item, idx) => (
//                             <button
//                                 key={`thumb-flex-${idx}`}
//                                 onClick={() => mainSwiper?.slideTo?.(idx)}
//                                 style={{
//                                     width: thumbSizePx,
//                                     height: thumbSizePx,
//                                     padding: 6,
//                                     borderRadius: 8,
//                                     border: "2px solid transparent",
//                                     background: "#fff",
//                                     overflow: "hidden",
//                                     display: "inline-flex",
//                                     alignItems: "center",
//                                     justifyContent: "center",
//                                     boxSizing: "border-box",
//                                     cursor: "pointer",
//                                 }}
//                                 className="thumb-wrapper"
//                                 aria-label={`thumbnail-${idx}`}
//                             >
//                                 <img
//                                     src={item.image}
//                                     alt={item.text || `thumb-${idx}`}
//                                     style={{
//                                         height: "100%",
//                                         width: "auto",
//                                         display: "block",
//                                         objectFit: "contain",
//                                         objectPosition: "center",
//                                         maxWidth: "100%",
//                                         maxHeight: "100%",
//                                         userSelect: "none",
//                                     }}
//                                     loading="lazy"
//                                     draggable={false}
//                                 />
//                             </button>
//                         ))}
//                     </div>
//                 ) : (
//                     // Swiper for 3+ thumbs with navigation arrows
//                     <>
//                         {/* Navigation buttons (positioned absolute inside container) */}
//                         <button
//                             ref={navPrevRef}
//                             className="thumb-nav-btn thumb-nav-prev"
//                             aria-label="Previous thumbnails"
//                             style={{ left: 2 }}
//                         >
//                             ‹
//                         </button>
//                         <button
//                             ref={navNextRef}
//                             className="thumb-nav-btn thumb-nav-next"
//                             aria-label="Next thumbnails"
//                             style={{ right: 2 }}
//                         >
//                             ›
//                         </button>

//                         <Swiper
//                             modules={[Thumbs, Navigation]}
//                             onSwiper={(swiper) => {
//                                 // save the swiper instance for optional external use
//                                 thumbsRef.current = swiper;
//                             }}
//                             thumbs={{ swiper: thumbsRef.current }}
//                             navigation={{
//                                 prevEl: navPrevRef.current,
//                                 nextEl: navNextRef.current,
//                             }}
//                             // This trick re-initializes navigation refs after mount
//                             onBeforeInit={(swiper) => {
//                                 if (typeof swiper.params.navigation !== "boolean") {
//                                     swiper.params.navigation.prevEl = navPrevRef.current;
//                                     swiper.params.navigation.nextEl = navNextRef.current;
//                                 }
//                             }}
//                             slidesPerView={Math.min(thumbSlidesPerView, thumbCount)}
//                             spaceBetween={thumbSpaceBetween}
//                             watchSlidesProgress
//                             freeMode={true}
//                             centeredSlides={false}
//                             slideToClickedSlide={true}
//                             style={{ padding: 0, overflow: "visible" }}
//                             breakpoints={{
//                                 // small screens: fewer visible thumbs
//                                 0: { slidesPerView: Math.min(3, thumbCount) },
//                                 480: { slidesPerView: Math.min(4, thumbCount) },
//                                 768: { slidesPerView: Math.min(thumbSlidesPerView, thumbCount) },
//                             }}
//                         >
//                             {images.map((item, idx) => (
//                                 <SwiperSlide
//                                     key={`thumb-${idx}`}
//                                     onClick={() => mainSwiper?.slideTo?.(idx)}
//                                     style={{
//                                         cursor: "pointer",
//                                         width: thumbSizePx,
//                                         height: thumbSizePx,
//                                         display: "flex",
//                                         alignItems: "center",
//                                         justifyContent: "center",
//                                         boxSizing: "border-box",
//                                         overflow: "visible",
//                                     }}
//                                 >
//                                     <div
//                                         style={{
//                                             border: "2px solid transparent",
//                                             borderRadius: 8,
//                                             overflow: "hidden",
//                                             width: "100%",
//                                             height: "100%",
//                                             display: "flex",
//                                             alignItems: "center",
//                                             justifyContent: "center",
//                                             background: "#fff",
//                                             padding: 6,
//                                             boxSizing: "border-box",
//                                         }}
//                                         className="thumb-wrapper"
//                                     >
//                                         <img
//                                             src={item.image}
//                                             alt={item.text || `thumb-${idx}`}
//                                             style={{
//                                                 height: "100%",
//                                                 width: "auto",
//                                                 display: "block",
//                                                 objectFit: "contain",
//                                                 objectPosition: "center",
//                                                 maxWidth: "100%",
//                                                 maxHeight: "100%",
//                                                 userSelect: "none",
//                                             }}
//                                             loading="lazy"
//                                             draggable={false}
//                                         />
//                                     </div>
//                                 </SwiperSlide>
//                             ))}
//                         </Swiper>
//                     </>
//                 )}
//             </div>

//             {/* minimal styles for arrows — put into your CSS or convert to Tailwind */}
//             <style jsx>{`
//         .thumb-nav-btn {
//           position: absolute;
//           top: 50%;
//           transform: translateY(-50%);
//           z-index: 10;
//           width: 34px;
//           height: 34px;
//           display: flex;
//           align-items: center;
//           justify-content: center;
//           border-radius: 6px;
//           border: none;
//           background: rgba(0, 0, 0, 0.6);
//           color: white;
//           cursor: pointer;
//           font-size: 20px;
//           line-height: 1;
//           box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
//           opacity: 0.9;
//         }
//         .thumb-nav-btn:focus {
//           outline: 2px solid #fff;
//           outline-offset: 2px;
//         }
//         /* hide arrows on very small screens if you prefer */
//         @media (max-width: 360px) {
//           .thumb-nav-btn {
//             display: none;
//           }
//         }
//       `}</style>
//         </div>
//     );
// }
